import { FC } from 'react';
import { UseFormReturn, Controller } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { AlertTriangle } from 'lucide-react';
import { MultiSelect } from '@/components/ui/multi-select';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { FlightSetupFormData, AOIOption, UserOption } from '../types/flight.types';
import type { MissionOption } from '../hooks/useFlightSetupForm';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface FlightSetupFormFieldsProps {
    form: UseFormReturn<FlightSetupFormData>;
    missionOptions: MissionOption[];
    aoiOptions: AOIOption[];
    userOptions: UserOption[];
    isValid: boolean;
    isLoadingMissions?: boolean;
    isLoadingAOIs?: boolean;
    isLoadingUsers?: boolean;
}

/**
 * Presentation component for Flight Setup form fields
 * Pure UI rendering - receives all data and handlers via props
 */
export const FlightSetupFormFields: FC<FlightSetupFormFieldsProps> = ({
    form,
    missionOptions,
    aoiOptions,
    userOptions,
    isValid,
    isLoadingMissions = false,
    isLoadingAOIs = false,
    isLoadingUsers = false,
}) => {
    const { register, control, formState: { errors } } = form;

    return (
        <>
            <div className="mb-6">
                <h2 className="text-lg font-medium text-white mb-1">Flight Parameters</h2>
                <p className="text-slate-400 text-sm">Define the basic flight configuration</p>
            </div>

            <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                {/* Flight Name */}
                <div className="space-y-2">
                    <Label>
                        Flight Name <span className="text-white">*</span>
                    </Label>
                    <Input
                        type="text"
                        placeholder="e.g., Downtown Infrastructure Scan"
                        {...register('flightName')}
                        className={cn(errors.flightName && "border-red-500 focus-visible:ring-red-500")}
                    />
                </div>

                {/* Select Mission */}
                <div className="space-y-2">
                    <Label>
                        Select Mission <span className="text-white">*</span>
                    </Label>
                    <Controller
                        name="selectMission"
                        control={control}
                        render={({ field }) => (
                            <Select onValueChange={field.onChange} value={field.value} disabled={isLoadingMissions}>
                                <SelectTrigger className={cn("w-full", errors.selectMission && "border-red-500 focus:ring-red-500")}>
                                    {isLoadingMissions ? (
                                        <span className="flex items-center gap-2 text-slate-400">
                                            Loading missions...
                                        </span>
                                    ) : (
                                        <SelectValue placeholder="Choose available mission" />
                                    )}
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        {missionOptions.length === 0 && !isLoadingMissions ? (
                                            <SelectItem value="__empty__" disabled>
                                                No missions available
                                            </SelectItem>
                                        ) : (
                                            missionOptions.map(m => (
                                                <SelectItem key={m.value} value={m.value}>
                                                    {m.label}
                                                </SelectItem>
                                            ))
                                        )}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        )}
                    />
                </div>

                {/* Select Area of Interest */}
                <div className="space-y-2">
                    <Label>
                        Select Area of Interest <span className="text-white">*</span>
                    </Label>

                    <Controller
                        name="selectAOIs"
                        control={control}
                        render={({ field }) => (
                            <MultiSelect
                                options={aoiOptions}
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Choose areas of interest"
                                disabled={isLoadingAOIs}
                                isLoading={isLoadingAOIs}
                                loadingText="Loading AOIs..."
                                emptyText="No AOIs available"
                                hasError={!!errors.selectAOIs}
                            />
                        )}
                    />
                </div>

                {/* Select Pilot */}
                <div className="space-y-2">
                    <Label>
                        Select Pilot <span className="text-white">*</span>
                    </Label>
                    <Controller
                        name="selectPilot"
                        control={control}
                        render={({ field }) => (
                            <Select onValueChange={field.onChange} value={field.value} disabled={isLoadingUsers}>
                                <SelectTrigger className={cn("w-full", errors.selectPilot && "border-red-500 focus:ring-red-500")}>
                                    {isLoadingUsers ? (
                                        <span className="flex items-center gap-2 text-slate-400">
                                            Loading users...
                                        </span>
                                    ) : (
                                        <SelectValue placeholder="Choose a pilot" />
                                    )}
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        {userOptions.length === 0 && !isLoadingUsers ? (
                                            <SelectItem value="__empty__" disabled>
                                                No users available
                                            </SelectItem>
                                        ) : (
                                            userOptions.map(u => (
                                                <SelectItem key={u.value} value={u.value}>
                                                    {u.label}
                                                </SelectItem>
                                            ))
                                        )}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        )}
                    />
                </div>

                {/* AI Enabled */}
                <div className="flex items-center space-x-3">
                    <Controller
                        name="aiEnabled"
                        control={control}
                        render={({ field }) => (
                            <Switch
                                id="aiEnabled"
                                checked={field.value}
                                onCheckedChange={field.onChange}
                            />
                        )}
                    />
                    <Label htmlFor="aiEnabled" className="cursor-pointer">
                        AI Enabled
                    </Label>
                </div>

                {/* Flight Description */}
                <div className="col-span-2 space-y-2">
                    <Label>Flight Description</Label>
                    <Textarea
                        placeholder="Describe the flight objectives and any special requirements..."
                        rows={4}
                        {...register('flightDescription')}
                    />
                </div>
            </div>

            {/* Validation Warning */}
            {!isValid && (
               <Alert variant="warning" className="mt-6 p-3 [&>svg]:top-3.5">
                    <AlertTriangle size={20} />
                    <AlertDescription className='mt-1.5 ml-1'>
                        Please fill in Flight Name, Select Mission, Select Area of Interest, and Select Pilot to continue
                    </AlertDescription>
                </Alert>
            )}
        </>
    );
};
